import { Avatar, Box, Checkbox, Flex, Highlight, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { ChangeEvent, useCallback } from 'react';
import { mapIcreatorToCreatorType } from '~/clientApi';
import { SavedList } from '~/clientApi/savedLists.model';
import { CategoryTag } from '~/components/CategoryTag';
import { GenderTag } from '~/components/GenderTag';
import WebLinks from '~/components/WebLinks';
import { CREATOR_MANAGER_RELATION } from '~/constants/creatorManagerRelation';
import { routes } from '~/core/router';
import { getMainPlatformData } from '~/core/utils';
import { useCurrentUserCreatorRelation } from '~/hooks/useCurrentUserCreatorRelation';
import { useGetCreatorLocation } from '~/hooks/useGetCreatorLocation';
import useImpersonateCreator from '~/hooks/useImpersonateCreator';
import { useListsStore } from '~/screens/WhalarModeListDetailScreen/store/useSavedListStore';
import { CreatorButtons } from '~/screens/WhalarModeScreen/components/StackView/CreatorInfo/CreatorButtons';
import { Platforms } from '~/screens/WhalarModeScreen/components/Table/Platforms';
import { useMarketerSearchTalentStore } from '~/screens/store';
import { ICreator } from '~/types';
import { convertPxToRems } from '~/utils-ts';

import { stackNameLinkStyle } from '../../styles';
import { ShareCreatorButton } from './ShareCreatorButton';
import { ViewProfileButton } from './ViewProfileButton';

interface ICreatorInfoMobile {
  list?: SavedList;
  creator: ICreator;
  note?: string;
  isProfile?: boolean;
  isShared?: boolean;
  onCheck?: (isChecked: boolean) => void;
  onNoteOpen?: () => void;
}

export const CreatorInfoMobile: React.FC<ICreatorInfoMobile> = ({
  list,
  creator,
  isProfile,
  isShared,
  onCheck,
  onNoteOpen,
  note,
}) => {
  const router = useRouter();

  const { age, bioAgency, slug, webLinks } = creator;
  const { avatar, name = '' } = getMainPlatformData(creator);
  const { getLocation } = useGetCreatorLocation();
  const creatorLocation = getLocation(creator);

  const { creators } = useListsStore();
  const { query } = useMarketerSearchTalentStore();

  const { setImpersonatedCreator } = useImpersonateCreator();
  const relation = useCurrentUserCreatorRelation(creator);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onCheck?.(event.target.checked);
    },
    [onCheck],
  );

  const handleCreatorClick = useCallback(async () => {
    if (!creator) return;

    if (isShared) {
      router.push(
        `https://${creator.slug}.${process.env.NEXT_PUBLIC_SHARED_PROFILES_HOST}`,
      );
      return;
    }

    if (
      relation === CREATOR_MANAGER_RELATION.manager ||
      relation === CREATOR_MANAGER_RELATION.coManager
    ) {
      await setImpersonatedCreator(mapIcreatorToCreatorType(creator));
      return;
    }

    router.push(routes.profileOverview(creator?.slug || ''));
  }, [creator, relation, isShared, router, setImpersonatedCreator]);

  const CreatorTools = () => (
    <Flex
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={4}
    >
      <CreatorButtons
        creator={creator}
        list={list}
        isShared={isShared}
        onNoteOpen={onNoteOpen}
        note={note}
      />
      {!isShared && slug && <ViewProfileButton slug={creator.slug} />}
      {slug && !isShared && isProfile && creator ? (
        <ShareCreatorButton creator={creator} />
      ) : null}
      {onCheck && !isProfile && !isShared && (
        <Flex onClick={(event) => event.stopPropagation()}>
          <Checkbox
            isChecked={!!creators.find((c) => c.id === creator.id)}
            onChange={handleChange}
          />
        </Flex>
      )}
    </Flex>
  );

  return (
    <Flex
      flexDirection={{ base: 'column', md: 'row' }}
      aria-label="contenedor general"
      display={{ lg: 'none' }}
    >
      <Box display={{ md: 'none' }}>
        <CreatorTools />
      </Box>
      <Flex flexDirection={{ base: 'column', md: 'row' }}>
        <Avatar
          marginTop={2}
          width={{ base: '100%', md: 40 }}
          height={{ base: '100%', md: 40 }}
          marginRight={{ base: 0, md: 4 }}
          marginBottom={{ base: 4, md: 0 }}
          borderRadius="lg"
          src={avatar}
        />
        <Flex flexDirection="column" w="full">
          <Flex
            style={{ textDecoration: 'none', cursor: 'pointer' }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Text
              fontSize="32px"
              fontWeight="500"
              color="primary.700"
              onClick={handleCreatorClick}
              {...(!isProfile && stackNameLinkStyle)}
            >
              <Highlight query={query} styles={{ color: 'secondary.500' }}>
                {name}
              </Highlight>
            </Text>
            <Box display={{ base: 'none', md: 'block' }}>
              <CreatorTools />
            </Box>
          </Flex>
          <Flex alignItems="center" gap={1} marginBottom={4}>
            {creatorLocation ? (
              <>
                <Text
                  fontWeight="400"
                  fontSize="md"
                  lineHeight={convertPxToRems(16)}
                  color="primary.700"
                >
                  {creatorLocation}
                </Text>
              </>
            ) : null}
            {age && (
              <Text
                fontWeight="400"
                fontSize="md"
                marginLeft={1}
                color="primary.700"
              >
                {age} years old
              </Text>
            )}
          </Flex>

          {creator.categoryAgency || creator.genderAgency ? (
            <Flex gap={3} marginBottom={4}>
              <CategoryTag creator={creator} />
              <GenderTag creator={creator} />
            </Flex>
          ) : null}

          <Flex alignItems="flex-end">
            <Platforms
              platforms={creator.platforms}
              isOffPlatform={creator.isOffPlatform}
              flexDirection="column"
              view="stacked"
            />
          </Flex>
          <Flex alignItems="flex-end" marginTop={3}>
            <WebLinks links={webLinks} />
          </Flex>
          {bioAgency && (
            <Text marginTop={4} fontSize="md" lineHeight={4}>
              {bioAgency}
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
